import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { getDoc, doc, collection, getDocs, query, where } from "firebase/firestore";
import { calculateOrderTotals } from "../utils/orderUtils";

const cardStyle = {
  background: "#ffffff",
  padding: 15,
  marginTop: 15,
  borderRadius: 10,
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
};

const styleTag = document.createElement("style");
styleTag.innerHTML = `
@keyframes pulse {
  0% { box-shadow: 0 0 10px gold; }
  50% { box-shadow: 0 0 20px gold; }
  100% { box-shadow: 0 0 10px gold; }
}`;
document.head.appendChild(styleTag);

const titleStyle = {
  marginBottom: 10,
  borderBottom: "1px solid #ddd",
  paddingBottom: 5
};

const thStyle = {
  padding: "10px",
  textAlign: "left",
  fontSize: "12px",
  color: "#4a5568",
  fontWeight: "bold",
  background: "#f4f5f7",
  borderBottom: "2px solid #e2e8f0"
};

const tdStyle = {
  padding: "10px",
  fontSize: "13px",
  color: "#2d3748",
  borderBottom: "1px solid #edf2f7"
};

export default function BasePage() {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [comms, setComms] = useState({
    schemeTitle: "📢 Schemes",
    schemes: [],
    earnings: [],
    winnerId: "",
    winnerName: "NAME",
    winnerRank: "🥇 Rank 1",
    winnerPhoto: "https://picsum.photos/100"
  });

  // Pending Orders States
  const [showPending, setShowPending] = useState(false);
  const [orders, setOrders] = useState([]);
  const [skuMaster, setSkuMaster] = useState({});
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    const loadComms = async () => {
      const user = auth.currentUser;
      if (!user) return;

      // Check Role
      const userSnap = await getDoc(doc(db, "users", user.uid));
      if (userSnap.exists()) {
        const uData = userSnap.data();
        setUserProfile(uData);
        const r = uData.role?.toLowerCase()?.trim();
        setRole(r);
      }

      // Load Communications
      const commSnap = await getDoc(doc(db, "settings", "hawkerCommunications"));
      if (commSnap.exists()) {
        const d = commSnap.data();
        let photo = "https://picsum.photos/100";
        let wName = d.winnerName || "NAME";

        if (d.winnerId) {
          const pSnap = await getDoc(doc(db, "partyMaster", d.winnerId));
          if (pSnap.exists()) {
            photo = pSnap.data().photoUrl || photo;
            wName = pSnap.data().name || wName;
          }
        }

        setComms({
          schemeTitle: d.schemeTitle || "📢 Schemes",
          schemes: d.schemes || [],
          earnings: d.earnings || [],
          winnerId: d.winnerId || "",
          winnerName: wName,
          winnerRank: d.winnerRank || "Rank 1",
          winnerPhoto: photo
        });
      }
    };
    loadComms();
  }, [auth.currentUser]);

  // Load Pending Orders when Toggled
  useEffect(() => {
    if (!showPending) return;

    async function loadPendingData() {
      setLoadingOrders(true);
      try {
        // 1. Fetch Sku Master definitions
        const skuSnap = await getDocs(collection(db, "skuMaster"));
        const sMap = {};
        skuSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.sku) sMap[data.sku.trim().toUpperCase()] = data;
        });
        setSkuMaster(sMap);

        // 2. Fetch Party Master to resolve party types
        const partySnap = await getDocs(collection(db, "partyMaster"));
        const pMap = {};
        partySnap.docs.forEach((d) => {
          const data = d.data();
          if (data.partyId) pMap[data.partyId] = data;
        });

        // 3. Fetch Pending Orders
        const q = query(collection(db, "orders"), where("status", "==", "PENDING"));
        const orderSnap = await getDocs(q);
        const list = orderSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Filter: only show partytype === "hawker"
        const filteredList = list.filter((order) => {
          const pData = pMap[order.partyId] || {};
          const pType = (pData.partytype || "").trim().toLowerCase();
          return pType === "hawker";
        });

        // Sort newest first
        filteredList.sort((a, b) => {
          const timeA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
          const timeB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0;
          return timeB - timeA;
        });

        setOrders(filteredList);
      } catch (err) {
        console.error("Error loading pending orders:", err);
      }
      setLoadingOrders(false);
    }
    loadPendingData();
  }, [showPending]);

  return (
    <div style={{ maxWidth: 400, margin: "auto", padding: 30 }}>

      <h2 style={{ textAlign: "center", marginBottom: 30 }}>
        DASHBOARD
      </h2>

      {/* ===== MAIN BUTTONS ===== */}

      <button
        onClick={() => navigate("/salesman-order")}
        style={{
          width: "100%",
          padding: 16,
          fontSize: 18,
          marginBottom: 15,
          background: "#1976d2",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          cursor: "pointer"
        }}
      >
        {(userProfile?.partytype || "HAWKER").toUpperCase() + " ORDER"}
      </button>

      <button
        onClick={() => navigate("/field-billing")}
        style={{
          width: "100%",
          padding: 16,
          fontSize: 18,
          marginBottom: 15,
          background: "#0d6efd",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          fontWeight: "bold",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}
      >
        📱 FIELD BILLING
      </button>

      <button
        onClick={() => navigate("/cashier")}
        style={{
          width: "100%",
          padding: 16,
          marginBottom: 15,
          background: "#2e7d32",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          cursor: "pointer"
        }}
      >
        COLLECTION
      </button>

      {/* ===== CDM BILLING PLACEHOLDER BUTTON ===== */}
      <button
        onClick={() => navigate("/cdm-billing")}
        style={{
          width: "100%",
          padding: 16,
          fontSize: 18,
          marginBottom: 15,
          background: "#673ab7",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          fontWeight: "bold"
        }}
      >
        🏢 CDM BILLING
      </button>

      {/* ===== NEW PENDING ORDERS TOGGLE BUTTON ===== */}
      <button
        onClick={() => setShowPending(!showPending)}
        style={{
          width: "100%",
          padding: 16,
          fontSize: 18,
          marginBottom: 15,
          background: showPending ? "#f57c00" : "#ff9800",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          fontWeight: "bold"
        }}
      >
        {showPending ? "⏳ HIDE PENDING ORDERS" : "⏳ PENDING ORDERS"}
      </button>

      {/* ===== EXPANDABLE PENDING ORDERS TABLE ===== */}
      {showPending && (
        <div style={{ ...cardStyle, overflowX: "auto", marginTop: 0, marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 10px 0", color: "#e65100" }}>Pending Approvals</h4>
          {loadingOrders ? (
            <div style={{ textAlign: "center", padding: "20px 0", fontSize: "14px", color: "#666" }}>Loading queue...</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>PARTY</th>
                  <th style={thStyle}>QTY</th>
                  <th style={thStyle}>VALUE</th>
                  <th style={thStyle}>SUBMITTED</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const { tGrand: orderAmt, tQty: qty } = calculateOrderTotals(order.items, skuMaster);
                  const formattedTime = order.createdAt?.seconds
                    ? new Date(order.createdAt.seconds * 1000).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit"
                      })
                    : "-";

                  return (
                    <tr key={order.id}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: "bold" }}>{order.partyName}</div>
                        <div style={{ fontSize: "11px", color: "#718096" }}>{order.partyId}</div>
                      </td>
                      <td style={tdStyle}>{qty.toFixed(2)}</td>
                      <td style={tdStyle}>₹{Math.round(orderAmt)}</td>
                      <td style={tdStyle}>{formattedTime}</td>
                    </tr>
                  );
                })}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ padding: "20px", textAlign: "center", color: "#718096", fontSize: "13px" }}>
                      No pending orders
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      <h2 style={{ textAlign: "center", marginBottom: 30, marginTop: 40 }}>
        {(userProfile?.partytype ? (userProfile.partytype.charAt(0).toUpperCase() + userProfile.partytype.slice(1)) : "Hawker") + " Communications"}
      </h2> 

      {/* ================= SCHEME COMMUNICATION ================= */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>{comms.schemeTitle}</h3>
        <ul style={{ paddingLeft: 18, fontSize: 16 }}>
          {comms.schemes.map((s, idx) => (
            <li key={idx}>{s}</li>
          ))}
          {comms.schemes.length === 0 && <p style={{ color: "gray", fontSize: 13 }}>No active schemes.</p>}
        </ul>
      </div>

      {/* ================= EARNING DETAILS ================= */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>💰 Earning Details</h3>
        {comms.earnings.map((e, idx) => (
          <p key={idx}><b>{idx+1}.</b> {e}</p>
        ))}
        {comms.earnings.length === 0 && <p style={{ color: "gray", fontSize: 13 }}>Details coming soon.</p>}
      </div>

      {/* ================= WINNER DASHBOARD ================= */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>🏆 Winner</h3>
        <div style={{ textAlign: "center" }}>
          <img 
            src={comms.winnerPhoto} 
            alt="Winner" 
            style={{ 
              width: 120, height: 120, borderRadius: "50%", 
              objectFit: "cover", 
              border: "3px solid gold", 
              boxShadow: "0 0 15px gold", 
              animation: "pulse 2s infinite"
            }}
          />
          <h4 style={{ marginTop: 10 }}>{comms.winnerName}</h4>
          <p style={{ color: "green", fontWeight: "bold" }}>{comms.winnerRank}</p>
        </div>
      </div>

    </div>
  );
}
